import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import loginService from "../services/login/login.js";
import logoutService from "../services/login/logout.js";
import validateToken from "../services/login/validate-token.js";
import validarTokenService from "../services/usuarios/validarToken.js";
import getEstadosPermitidosService from "../services/turnos/getEstadosPermitidos.js";
import getPerfilesService from "../services/usuarios/getPerfiles.js";
import { toast } from "react-toastify";

const AuthContext = createContext(null);

const extractPermissions = (perfiles) => {
    if (!Array.isArray(perfiles) || perfiles.length === 0) return null;
    return [
        ...new Set(
            perfiles.flatMap(p => p.modulos.map(m => m.nombre.toLowerCase()))
        )
    ];
};

const extractAcciones = (perfiles) => {
    if (!Array.isArray(perfiles) || perfiles.length === 0) return [];
    return [
        ...new Set(
            perfiles.flatMap((p) => (p.acciones || []).map((a) => String(a).toLowerCase()))
        ),
    ];
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('authToken'));
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(() => !!localStorage.getItem('authToken'));
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [estadosVisibles, setEstadosVisibles] = useState([]);
    const [acciones, setAcciones] = useState([]);
    const [refreshMenuTrigger, setRefreshMenuTrigger] = useState(0);
    const userRef = useRef(null);
    const allPerfilesCacheRef = useRef([]);
    useEffect(() => { userRef.current = user; }, [user]);

    const applyPerfilesToUser = useCallback((userData, perfiles, rawPerfilesData) => {
        const moduleNames = extractPermissions(perfiles);
        setAcciones(extractAcciones(perfiles));
        setUser({
            ...userData,
            permissions: moduleNames,
            rawPerfiles: rawPerfilesData,
        });
        setPermissionsLoaded(true);
    }, []);

    const triggerMenuRefresh = useCallback((newProfileIds = null) => {
        if (newProfileIds != null) {
            const newIds = newProfileIds.map(String);

            const allCached = allPerfilesCacheRef.current;
            if (allCached.length > 0) {
                const targets = allCached.filter(p => newIds.includes(String(p.idPerfil)));
                if (targets.length === newIds.length && targets.every(p => Array.isArray(p.modulos))) {
                    const moduleNames = extractPermissions(targets) || [];
                    const newRawPerfiles = { data: { authenticated_user: { perfiles: targets } } };
                    setAcciones(extractAcciones(targets));
                    setUser(u => ({ ...u, permissions: moduleNames, rawPerfiles: newRawPerfiles }));
                    return;
                }
            }

            const userCached = userRef.current;
            if (userCached?.rawPerfiles) {
                const known = userCached.rawPerfiles?.data?.authenticated_user?.perfiles || [];
                const knownIds = new Set(known.map(p => String(p.idPerfil)));
                if (newIds.every(id => knownIds.has(id))) {
                    const newPerfiles = known.filter(p => newIds.includes(String(p.idPerfil)));
                    const moduleNames = extractPermissions(newPerfiles) || [];
                    setAcciones(extractAcciones(newPerfiles));
                    setUser(u => ({
                        ...u,
                        permissions: moduleNames,
                        rawPerfiles: {
                            ...userCached.rawPerfiles,
                            data: {
                                ...userCached.rawPerfiles?.data,
                                authenticated_user: {
                                    ...userCached.rawPerfiles?.data?.authenticated_user,
                                    perfiles: newPerfiles,
                                },
                            },
                        },
                    }));
                    return;
                }
            }
        }

        setRefreshMenuTrigger(t => t + 1);
    }, []);

    const refreshPermissions = useCallback(async () => {
        try {
            const perfilesResponse = await validarTokenService().catch(() => null);
            const perfiles = perfilesResponse?.data?.data?.authenticated_user?.perfiles;
            const moduleNames = extractPermissions(perfiles);
            if (!moduleNames) return;
            setAcciones(extractAcciones(perfiles));
            setUser(u => ({ ...u, permissions: moduleNames, rawPerfiles: perfilesResponse.data }));
            setPermissionsLoaded(true);
        } catch (error) {
            console.error("Error refreshing permissions:", error);
        }
    }, []);

    const validateCurrentToken = useCallback(async () => {
        const currentToken = token || localStorage.getItem('authToken');
        if (!currentToken) {
            setIsAuthenticated(false);
            setUser(null);
            setPermissionsLoaded(false);
            setAcciones([]);
            return false;
        }
        try {
            setIsLoading(true);

            const [authResponse, perfilesResponse, estadosResponse, allPerfilesResponse] = await Promise.all([
                validateToken({ token: currentToken }),
                validarTokenService().catch(() => null),
                getEstadosPermitidosService().catch(() => null),
                getPerfilesService().catch(() => null),
            ]);

            if (!authResponse.data?.data?.authenticated_user) {
                setUser(null);
                setIsAuthenticated(false);
                setToken(null);
                setPermissionsLoaded(false);
                setAcciones([]);
                localStorage.removeItem('authToken');
                return false;
            }

            const authenticatedUser = authResponse.data.data.authenticated_user;
            const userData = {
                userId: authenticatedUser.id,
                userFullName: authenticatedUser.fullname,
            };

            setIsAuthenticated(true);
            if (!token) setToken(currentToken);

            const estadosData = estadosResponse?.data;
            if (estadosData) {
                setEstadosVisibles(Array.isArray(estadosData) ? estadosData : []);
            }

            const allPerfilesData = allPerfilesResponse?.data?.resultados || [];
            const currentUserPerfiles = perfilesResponse?.data?.data?.authenticated_user?.perfiles || [];
            const cacheHasModules = allPerfilesData.some(p => Array.isArray(p.modulos) && p.modulos.length > 0);
            const cacheMatchesUser = currentUserPerfiles.length === 0 || currentUserPerfiles.every(up => {
                const cached = allPerfilesData.find(ap => Number(ap.idPerfil) === Number(up.idPerfil));
                return cached && Array.isArray(cached.modulos) && cached.modulos.length === (up.modulos?.length ?? 0);
            });
            if (allPerfilesData.length > 0 && cacheHasModules && cacheMatchesUser) {
                allPerfilesCacheRef.current = allPerfilesData;
            }

            const perfiles = perfilesResponse?.data?.data?.authenticated_user?.perfiles;
            if (!Array.isArray(perfiles) || perfiles.length === 0) {
                toast.warning("Tu cuenta aún no tiene un perfil asignado. Contacta con el administrador.", { position: "top-right" });
                setUser(userData);
                setAcciones([]);
                setPermissionsLoaded(true);
                return true;
            }

            if (perfiles.length === 1 && perfiles[0]?.modulos?.length === 0) {
                toast.warning("Tu perfil no tiene permisos configurados. Contacta con el administrador.", { position: "top-right" });
                setUser(userData);
                setAcciones([]);
                setPermissionsLoaded(true);
                return true;
            }

            applyPerfilesToUser(userData, perfiles, perfilesResponse.data);
            return true;
        } catch (error) {
            console.error("Error validating token:", error);
            setUser(null);
            setIsAuthenticated(false);
            setToken(null);
            setPermissionsLoaded(false);
            setAcciones([]);
            localStorage.removeItem('authToken');
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [token, applyPerfilesToUser]);

    useEffect(() => {
        if (!token) {
            setIsAuthenticated(false);
            setUser(null);
            setPermissionsLoaded(false);
            setAcciones([]);
            localStorage.removeItem('authToken');
            setIsLoading(false);
            return;
        }
        validateCurrentToken();
    }, [token, validateCurrentToken]);

    useEffect(() => {
        if (!refreshMenuTrigger || !isAuthenticated) return;
        refreshPermissions();
    }, [refreshMenuTrigger, isAuthenticated, refreshPermissions]);

    const login = useCallback(async ({ email, password }) => {
        try {
            const response = await loginService({ email, password });
            if (!response.data?.userId) {
                throw new Error(response.data?.description || "Error al iniciar sesión. Inténtalo de nuevo.");
            }
            const { userId, userFullName, token: newToken } = response.data;
            const userData = { userId, userFullName };
            setIsLoading(true);
            setToken(newToken);
            localStorage.setItem('authToken', newToken);
            return userData;
        } catch (error) {
            console.error("Error en login:", error);
            throw error;
        }
    }, []);

    const logout = useCallback(async (clientId = null) => {
        try {
            await logoutService(clientId);
        } catch (error) {
            console.error("Error during logout:", error);
        } finally {
            setUser(null);
            setIsAuthenticated(false);
            setToken(null);
            setPermissionsLoaded(false);
            setAcciones([]);
            setIsLoading(false);
            localStorage.removeItem('authToken');
        }
    }, []);

    const value = useMemo(
        () => ({
            user,
            login,
            logout,
            isAuthenticated,
            isLoading,
            permissionsLoaded,
            acciones,
            estadosVisibles,
            refreshMenuTrigger,
            triggerMenuRefresh,
        }),
        [user, isAuthenticated, isLoading, permissionsLoaded, acciones, login, logout, estadosVisibles, refreshMenuTrigger, triggerMenuRefresh]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
